/**
 * Fog of War — pure functions for per-player visibility.
 * No Three.js imports. Operates on FogState types from types.ts.
 */

import { contentChanged } from './contentHash';
import type { FogState, FogPlanetState, GhostMarker, StructureGhost, ExposureMarker, PlanetState, PlayerState, StructureState, Vec3, GameState } from '../types';
import { perceptionFilter } from './perception';
import { sub, dot, normalize, length, lengthSq, scale, add } from '../math';
import { wasmCalculatePlayerLOS, wasmGetVisibleEnemies } from '../ai/wasmPhysics';
import { FOG_STALE_TURNS, FOG_STALE_FADE_TURNS, FOG_STALE_MIN_BRIGHTNESS } from '../constants';
import {
  losEyePoint, eyePointAt, playerSurfacePoint, hasLineOfSight, buildSkyline, skylineBlocks,
  ownPlanetRevealAngle, terrainPeekAngle, terrainShadowMask, getEffectiveBlockingRadius,
} from './los';
import { dacos } from './dmath';

export { getEffectiveBlockingRadius } from './los';

// =============================================================================
// INITIALIZATION
// =============================================================================

export function initializeFog(numPlayers: number, planets: PlanetState[]): FogState[] {
  const fogStates: FogState[] = [];
  for (let p = 0; p < numPlayers; p++) {
    const planetFogs: FogPlanetState[] = [];
    for (const planet of planets) {
      const vertexCount = planet.heightmap.length;
      const alwaysRevealed = planet.type === 'star' || planet.type === 'wormhole';
      planetFogs.push({
        revealedVertices: new Array(vertexCount).fill(alwaysRevealed),
        terrainSnapshot: new Float32Array(vertexCount),
        colorSnapshot: new Float32Array(vertexCount * 3),
        revealTurn: new Uint16Array(vertexCount),
      });
    }
    fogStates.push({
      planets: planetFogs,
      ghosts: [],
      structureGhosts: [],
      exposures: [],
    });
  }
  return fogStates;
}

// =============================================================================
// RAY-SPHERE INTERSECTION
// =============================================================================

/**
 * Check if a ray from `origin` in direction `dir` intersects a sphere.
 * Used to check if LOS is blocked by intervening planets.
 * `dir` does not need to be normalized — we normalize internally.
 * Returns true if the ray intersects the sphere between origin and the target.
 */
export function raySphereIntersects(
  origin: Vec3,
  dir: Vec3,
  center: Vec3,
  radius: number,
  maxDist?: number
): boolean {
  const oc = sub(origin, center);
  const dirNorm = normalize(dir);
  const a = dot(dirNorm, dirNorm); // ~1 since normalized
  const b = 2 * dot(oc, dirNorm);
  const c = dot(oc, oc) - radius * radius;

  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return false;

  const sqrtDisc = Math.sqrt(discriminant);
  const t1 = (-b - sqrtDisc) / (2 * a);
  const t2 = (-b + sqrtDisc) / (2 * a);

  // If origin is inside sphere, t1 < 0, t2 > 0 → intersects
  // We want intersection between origin and target (maxDist)
  const limit = maxDist !== undefined ? maxDist : Infinity;

  // Check if any intersection is in the valid range (slightly past origin, before target)
  if (t1 > 0.01 && t1 < limit) return true;
  if (t2 > 0.01 && t2 < limit) return true;

  return false;
}

// =============================================================================
// LINE-OF-SIGHT
// =============================================================================

/**
 * Check if a vertex world position is visible from a viewer position,
 * not blocked by any of the given planets.
 */
export function isVertexVisibleFromPos(
  viewerPos: Vec3,
  vertexWorldPos: Vec3,
  blockingPlanets: PlanetState[],
  blockingRadii?: number[]
): boolean {
  const toVertex = sub(vertexWorldPos, viewerPos);
  const dist = length(toVertex);
  if (dist < 0.001) return true;

  const dir = normalize(toVertex);

  for (let i = 0; i < blockingPlanets.length; i++) {
    const planet = blockingPlanets[i];
    const radius = blockingRadii ? blockingRadii[i] : planet.radius;
    if (raySphereIntersects(viewerPos, dir, planet.position, radius, dist)) {
      return false;
    }
  }

  return true;
}

/**
 * Which vertices of `planet` a viewer whose eye is at `eye` can see.
 *
 * Own planet: the survey cap (`ownPlanetRevealAngle`) plus each vertex's
 * terrain peek. Remote planet: the geometric silhouette, that planet's own
 * ridge shadows, the viewer's own-planet skyline, and third-party planets as
 * spheres. Mirrored by `calculate_player_los` in lib.rs.
 */
export function calculatePlayerLOS(
  eye: Vec3,
  playerPlanetIndex: number,
  targetPlanetIndex: number,
  planet: PlanetState,
  allPlanets: PlanetState[],
  useWasm?: boolean,
): number[] {
  if (useWasm) {
    const result = wasmCalculatePlayerLOS(eye, playerPlanetIndex, targetPlanetIndex);
    if (result) return result;
  }
  const visibleIndices: number[] = [];
  const vertexCount = planet.heightmap.length;
  const bp = planet.basePositions;
  const hm = planet.heightmap;

  if (playerPlanetIndex === targetPlanetIndex) {
    const toEye = sub(eye, planet.position);
    const eyeDist = length(toEye);
    const eyeDir = eyeDist > 0.001 ? normalize(toEye) : { x: 0, y: 1, z: 0 };
    const revealAngle = ownPlanetRevealAngle(planet, Math.max(0, eyeDist - planet.radius));

    for (let i = 0; i < vertexCount; i++) {
      const bx = bp[i * 3], by = bp[i * 3 + 1], bz = bp[i * 3 + 2];
      const bLen = Math.sqrt(bx * bx + by * by + bz * bz);
      const cosAngle = Math.max(-1, Math.min(1, (bx * eyeDir.x + by * eyeDir.y + bz * eyeDir.z) / bLen));
      const angle = dacos(cosAngle);
      if (angle <= revealAngle + terrainPeekAngle(planet, hm[i])) {
        visibleIndices.push(i);
      }
    }
    return visibleIndices;
  }

  // Third-party planets block as spheres that enclose their tallest terrain.
  // The viewer's own planet is the skyline; the target planet is its silhouette + shadow mask.
  const blockingPlanets: PlanetState[] = [];
  const blockingRadii: number[] = [];
  for (let i = 0; i < allPlanets.length; i++) {
    if (i === targetPlanetIndex || i === playerPlanetIndex) continue;
    if (!allPlanets[i].alive) continue;
    blockingPlanets.push(allPlanets[i]);
    blockingRadii.push(getEffectiveBlockingRadius(allPlanets[i]));
  }
  const ownPlanet = allPlanets[playerPlanetIndex];
  const skyline = ownPlanet && ownPlanet.alive ? buildSkyline(eye, ownPlanet) : null;
  const shadow = terrainShadowMask(eye, planet);

  // Geometric silhouette: a vertex at direction D from the centre is visible from
  // distance V if dot(D, viewerDir) > R/V; terrain at height h peeks a little further.
  const toViewer = sub(eye, planet.position);
  const viewerDist = length(toViewer);
  const viewerDirNorm = viewerDist > 0.001 ? normalize(toViewer) : { x: 0, y: 1, z: 0 };
  const tangentThreshold = planet.radius / viewerDist;

  for (let i = 0; i < vertexCount; i++) {
    const bx = bp[i * 3], by = bp[i * 3 + 1], bz = bp[i * 3 + 2];
    const bLen = Math.sqrt(bx * bx + by * by + bz * bz);
    const nx = bx / bLen, ny = by / bLen, nz = bz / bLen;
    const h = hm[i];

    const cosAngle = nx * viewerDirNorm.x + ny * viewerDirNorm.y + nz * viewerDirNorm.z;
    const threshold = h > 0 ? planet.radius / (viewerDist + h) : tangentThreshold;
    if (cosAngle <= threshold) continue;   // behind the target planet's limb
    if (shadow[i]) continue;               // behind one of its own ridges

    const vertexWorld: Vec3 = {
      x: planet.position.x + bx + nx * h,
      y: planet.position.y + by + ny * h,
      z: planet.position.z + bz + nz * h,
    };
    if (skyline && skylineBlocks(skyline, vertexWorld)) continue;  // below the viewer's own horizon
    if (isVertexVisibleFromPos(eye, vertexWorld, blockingPlanets, blockingRadii)) {
      visibleIndices.push(i);
    }
  }

  return visibleIndices;
}

// =============================================================================
// VERTEX REVEAL
// =============================================================================

/**
 * Reveal specified vertices on a planet, snapshotting their current state.
 * Returns a new FogPlanetState with the revealed vertices updated.
 */
export function revealVertices(
  fogPlanet: FogPlanetState,
  vertexIndices: number[],
  planet: PlanetState,
  currentColors: Float32Array,
  turnNumber: number
): FogPlanetState {
  // Clone arrays for immutability
  const revealedVertices = [...fogPlanet.revealedVertices];
  const terrainSnapshot = new Float32Array(fogPlanet.terrainSnapshot);
  const colorSnapshot = new Float32Array(fogPlanet.colorSnapshot);
  const revealTurn = new Uint16Array(fogPlanet.revealTurn);

  for (const i of vertexIndices) {
    revealedVertices[i] = true;
    terrainSnapshot[i] = planet.heightmap[i];
    colorSnapshot[i * 3] = currentColors[i * 3];
    colorSnapshot[i * 3 + 1] = currentColors[i * 3 + 1];
    colorSnapshot[i * 3 + 2] = currentColors[i * 3 + 2];
    revealTurn[i] = turnNumber;
  }

  return { revealedVertices, terrainSnapshot, colorSnapshot, revealTurn };
}

/**
 * True colours changed on a planet (crater, mound): rewrite the snapshot of every
 * vertex this player has already revealed, so exposed strata and scorch read the
 * same under fog as in true colours. Unrevealed vertices stay unknown. Returns the
 * same object when nothing was revealed there.
 */
export function refreshFogColors(fog: FogState, planetIndex: number, colors: Float32Array): FogState {
  const fp = fog.planets[planetIndex];
  if (!fp) return fog;
  let any = false;
  for (let i = 0; i < fp.revealedVertices.length; i++) if (fp.revealedVertices[i]) { any = true; break; }
  if (!any) return fog;
  const snap = new Float32Array(fp.colorSnapshot);
  for (let i = 0; i < fp.revealedVertices.length; i++) {
    if (!fp.revealedVertices[i]) continue;
    snap[i * 3] = colors[i * 3]; snap[i * 3 + 1] = colors[i * 3 + 1]; snap[i * 3 + 2] = colors[i * 3 + 2];
  }
  const planets = [...fog.planets];
  planets[planetIndex] = { ...fp, colorSnapshot: snap };
  return { ...fog, planets };
}

/**
 * 62.56 (decision 136, the dig's cost): `refreshFogColors` at the vertices whose colour changed — the same fog, for a
 * fraction of the work. Every fog's revealed vertices already hold the true colours (a reveal writes them, and every
 * change of the true colours refreshes here), so only a changed vertex can differ; a fog that has revealed none of them
 * is returned as it is. (A refresh of every revealed vertex in all 50 of a 25-a-side match's fogs was 5.5 ms a shell.)
 */
export function refreshFogColorsAt(fog: FogState, planetIndex: number, colors: Float32Array, changed: readonly number[]): FogState {
  const fp = fog.planets[planetIndex];
  if (!fp || changed.length === 0) return fog;
  let snap: Float32Array | null = null;
  for (const i of changed) {
    if (!fp.revealedVertices[i]) continue;
    snap ??= new Float32Array(fp.colorSnapshot);
    snap[i * 3] = colors[i * 3]; snap[i * 3 + 1] = colors[i * 3 + 1]; snap[i * 3 + 2] = colors[i * 3 + 2];
  }
  if (!snap) return fog;
  const planets = [...fog.planets];
  planets[planetIndex] = { ...fp, colorSnapshot: snap };
  return { ...fog, planets };
}

/**
 * `changedVertices` looked for only among `among` (ascending): the same list when every vertex whose colour changed is
 * among them — a crater's recoloured facets (core/planetColors.ts recolouredFacets), not the whole planet's 61,440.
 */
export function changedVerticesAmong(before: Float32Array, after: Float32Array, among: readonly number[]): number[] {
  const out: number[] = [], n = Math.min(before.length, after.length) / 3;
  for (const i of among) {
    if (i >= n) break;
    if (before[i * 3] !== after[i * 3] || before[i * 3 + 1] !== after[i * 3 + 1] || before[i * 3 + 2] !== after[i * 3 + 2]) out.push(i);
  }
  return out;
}

/** 62.56: the vertices whose colour differs between two colour arrays of one planet (r, g, b a vertex). */
export function changedVertices(before: Float32Array, after: Float32Array): number[] {
  const out: number[] = [];
  for (let i = 0, n = Math.min(before.length, after.length) / 3; i < n; i++) {
    if (before[i * 3] !== after[i * 3] || before[i * 3 + 1] !== after[i * 3 + 1] || before[i * 3 + 2] !== after[i * 3 + 2]) out.push(i);
  }
  return out;
}

// =============================================================================
// PER-TURN FOG UPDATE
// =============================================================================

/**
 * Update fog for a single player: recalculate LOS from current position
 * and reveal visible vertices.
 */
export function updateFogForPlayer(
  fogState: FogState,
  player: PlayerState,
  planets: PlanetState[],
  planetColors: Float32Array[],
  turnNumber: number,
  useWasm?: boolean,
  darkenedPlanets?: number[],
  darkVisibilityRadius?: number,
): FogState {
  const playerPos = losEyePoint(player, planets);
  const newPlanets = [...fogState.planets];
  const darkenedSet = darkenedPlanets ? new Set(darkenedPlanets) : null;

  for (let pi = 0; pi < planets.length; pi++) {
    const planet = planets[pi];
    if (!planet.alive) continue;

    // Dark planet: only reveal vertices near friendly units on that planet
    if (darkenedSet && darkenedSet.has(pi)) {
      if (player.planetIndex !== pi) {
        // Player NOT on this dark planet — sees nothing
        continue;
      }
      // Player IS on this dark planet — reveal only within dark visibility radius
      const radius = darkVisibilityRadius ?? 2.0;
      const localVisible = getVerticesWithinRadius(playerPos, planet, radius);
      if (localVisible.length > 0) {
        newPlanets[pi] = revealVertices(
          newPlanets[pi],
          localVisible,
          planet,
          planetColors[pi],
          turnNumber
        );
      }
      continue;
    }

    const visibleIndices = calculatePlayerLOS(
      playerPos,
      player.planetIndex,
      pi,
      planet,
      planets,
      useWasm,
    );

    if (visibleIndices.length > 0) {
      newPlanets[pi] = revealVertices(
        newPlanets[pi],
        visibleIndices,
        planet,
        planetColors[pi],
        turnNumber
      );
    }
  }

  return { ...fogState, planets: newPlanets };
}

/**
 * Get vertex indices within a world-space radius of a point on a planet.
 * Used for dark planet local visibility.
 */
function getVerticesWithinRadius(
  center: Vec3,
  planet: PlanetState,
  radius: number,
): number[] {
  const radiusSq = radius * radius;
  const indices: number[] = [];
  const vertexCount = planet.heightmap.length;

  for (let i = 0; i < vertexCount; i++) {
    const bx = planet.basePositions[i * 3];
    const by = planet.basePositions[i * 3 + 1];
    const bz = planet.basePositions[i * 3 + 2];
    const bLen = Math.sqrt(bx * bx + by * by + bz * bz);
    const nx = bx / bLen;
    const ny = by / bLen;
    const nz = bz / bLen;
    const h = planet.heightmap[i];

    const vx = planet.position.x + bx + nx * h;
    const vy = planet.position.y + by + ny * h;
    const vz = planet.position.z + bz + nz * h;

    const dx = vx - center.x;
    const dy = vy - center.y;
    const dz = vz - center.z;
    if (dx * dx + dy * dy + dz * dz <= radiusSq) {
      indices.push(i);
    }
  }

  return indices;
}

// =============================================================================
// PROBE REVEAL
// =============================================================================

/**
 * Does a vertex face a scanner at `scannerDist` from the planet centre
 * (direction `(sx, sy, sz)`, unnormalised)? The geometric silhouette:
 * dot(vertexDir, scannerDir) > R / dist, with terrain at height h peeking a
 * little further. A scanner reveals the cap it can see, not the whole ball
 * within range — a probe skimming one side no longer maps the far side.
 */
function facesScanner(
  nx: number, ny: number, nz: number, h: number, radius: number,
  sx: number, sy: number, sz: number, scannerDist: number,
): boolean {
  if (scannerDist <= 1e-9) return false;
  const cosAngle = (nx * sx + ny * sy + nz * sz) / scannerDist;
  const threshold = h > 0 ? radius / (scannerDist + h) : radius / scannerDist;
  return cosAngle > threshold;
}

/**
 * Reveal terrain around a single point (e.g. current probe position).
 * Used for real-time reveal during probe flight.
 * Mutates fogState in place for performance (caller should clone if needed).
 * Returns true if any new vertices were revealed.
 */
export function revealAtPoint(
  fogState: FogState,
  point: Vec3,
  planets: PlanetState[],
  revealRadius: number,
  planetColors: Float32Array[],
  turnNumber: number
): boolean {
  const revealRadiusSq = revealRadius * revealRadius;
  let anyRevealed = false;

  for (let pi = 0; pi < planets.length; pi++) {
    const planet = planets[pi];
    if (!planet.alive) continue;

    // Quick bounding check: is the point anywhere near this planet?
    const dx0 = point.x - planet.position.x;
    const dy0 = point.y - planet.position.y;
    const dz0 = point.z - planet.position.z;
    const distToPlanetSq = dx0 * dx0 + dy0 * dy0 + dz0 * dz0;
    const maxReach = planet.radius + revealRadius + 1;
    if (distToPlanetSq > maxReach * maxReach) continue;
    const pointDist = Math.sqrt(distToPlanetSq);

    const fp = fogState.planets[pi];
    const colors = planetColors[pi];
    const vertexCount = planet.heightmap.length;
    let planetTouched = false;

    for (let i = 0; i < vertexCount; i++) {
      if (fp.revealedVertices[i]) continue;

      const bx = planet.basePositions[i * 3];
      const by = planet.basePositions[i * 3 + 1];
      const bz = planet.basePositions[i * 3 + 2];
      const bLen = Math.sqrt(bx * bx + by * by + bz * bz);
      const nx = bx / bLen;
      const ny = by / bLen;
      const nz = bz / bLen;
      const h = planet.heightmap[i];
      if (!facesScanner(nx, ny, nz, h, planet.radius, dx0, dy0, dz0, pointDist)) continue;

      const vx = planet.position.x + bx + nx * h;
      const vy = planet.position.y + by + ny * h;
      const vz = planet.position.z + bz + nz * h;

      const ddx = vx - point.x;
      const ddy = vy - point.y;
      const ddz = vz - point.z;
      if (ddx * ddx + ddy * ddy + ddz * ddz <= revealRadiusSq) {
        fp.revealedVertices[i] = true;
        fp.terrainSnapshot[i] = planet.heightmap[i];
        fp.colorSnapshot[i * 3] = colors[i * 3];
        fp.colorSnapshot[i * 3 + 1] = colors[i * 3 + 1];
        fp.colorSnapshot[i * 3 + 2] = colors[i * 3 + 2];
        fp.revealTurn[i] = turnNumber;
        anyRevealed = true;
        planetTouched = true;
      }
    }
    // in-place edits: the per-tick checksum's cached hashes of these arrays are stale now
    if (planetTouched) contentChanged(fp.revealedVertices, fp.terrainSnapshot, fp.colorSnapshot, fp.revealTurn);
  }

  return anyRevealed;
}

/**
 * Reveal terrain along a probe's flight path.
 * For each sampled path point, reveals vertices within revealRadius of any planet.
 */
export function revealAlongPath(
  fogState: FogState,
  pathPoints: Vec3[],
  planets: PlanetState[],
  revealRadius: number,
  planetColors: Float32Array[],
  turnNumber: number
): FogState {
  const revealRadiusSq = revealRadius * revealRadius;
  const newPlanets = fogState.planets.map(fp => ({
    revealedVertices: [...fp.revealedVertices],
    terrainSnapshot: new Float32Array(fp.terrainSnapshot),
    colorSnapshot: new Float32Array(fp.colorSnapshot),
    revealTurn: new Uint16Array(fp.revealTurn),
  }));

  // Sample every 5th point for performance
  const step = Math.max(1, Math.floor(pathPoints.length / 200));

  for (let ptIdx = 0; ptIdx < pathPoints.length; ptIdx += step) {
    const point = pathPoints[ptIdx];

    for (let pi = 0; pi < planets.length; pi++) {
      const planet = planets[pi];
      if (!planet.alive) continue;

      // Quick check: is the point anywhere near this planet?
      const toPlanet = sub(point, planet.position);
      const distToPlanetSq = lengthSq(toPlanet);
      const maxReach = planet.radius + revealRadius + 1;
      if (distToPlanetSq > maxReach * maxReach) continue;
      const pointDist = Math.sqrt(distToPlanetSq);

      const vertexCount = planet.heightmap.length;
      const colors = planetColors[pi];
      const fp = newPlanets[pi];

      for (let i = 0; i < vertexCount; i++) {
        if (fp.revealedVertices[i]) continue; // Already revealed

        const bx = planet.basePositions[i * 3];
        const by = planet.basePositions[i * 3 + 1];
        const bz = planet.basePositions[i * 3 + 2];
        const bLen = Math.sqrt(bx * bx + by * by + bz * bz);
        const nx = bx / bLen;
        const ny = by / bLen;
        const nz = bz / bLen;
        const h = planet.heightmap[i];
        if (!facesScanner(nx, ny, nz, h, planet.radius, toPlanet.x, toPlanet.y, toPlanet.z, pointDist)) continue;

        const vx = planet.position.x + bx + nx * h;
        const vy = planet.position.y + by + ny * h;
        const vz = planet.position.z + bz + nz * h;

        const dx = vx - point.x;
        const dy = vy - point.y;
        const dz = vz - point.z;
        const dSq = dx * dx + dy * dy + dz * dz;

        if (dSq <= revealRadiusSq) {
          fp.revealedVertices[i] = true;
          fp.terrainSnapshot[i] = planet.heightmap[i];
          fp.colorSnapshot[i * 3] = colors[i * 3];
          fp.colorSnapshot[i * 3 + 1] = colors[i * 3 + 1];
          fp.colorSnapshot[i * 3 + 2] = colors[i * 3 + 2];
          fp.revealTurn[i] = turnNumber;
        }
      }
    }
  }

  return { ...fogState, planets: newPlanets };
}

// =============================================================================
// VISIBLE ENEMIES
// =============================================================================

/** Can a scanner at `point` see this player: in range, and nothing in between? */
function scannerSeesPlayer(point: Vec3, target: PlayerState, planets: PlanetState[], revealRadiusSq: number): boolean {
  const eye = losEyePoint(target, planets);
  const dx = eye.x - point.x, dy = eye.y - point.y, dz = eye.z - point.z;
  if (dx * dx + dy * dy + dz * dz > revealRadiusSq) return false;
  return hasLineOfSight(point, eye, planets);
}

/**
 * Enemies a scanner (probe, airborne player) at `point` can see: in range and
 * with a clear line — not through the planet they stand behind. Teammates are
 * never "spotted".
 */
export function getEnemiesAtPoint(
  viewerPlayer: PlayerState,
  allPlayers: PlayerState[],
  planets: PlanetState[],
  point: Vec3,
  revealRadius: number
): number[] {
  const revealRadiusSq = revealRadius * revealRadius;
  const spottedIds: number[] = [];
  for (const enemy of allPlayers) {
    if (enemy.id === viewerPlayer.id) continue;
    if (!enemy.alive) continue;
    if (enemy.teamIndex === viewerPlayer.teamIndex) continue;
    if (scannerSeesPlayer(point, enemy, planets, revealRadiusSq)) spottedIds.push(enemy.id);
  }
  return spottedIds;
}

/**
 * Enemies seen from any sampled point along a scanner's path.
 */
export function getEnemiesAlongPath(
  viewerPlayer: PlayerState,
  allPlayers: PlayerState[],
  planets: PlanetState[],
  pathPoints: Vec3[],
  revealRadius: number
): number[] {
  const revealRadiusSq = revealRadius * revealRadius;
  const spottedIds: Set<number> = new Set();
  const step = Math.max(1, Math.floor(pathPoints.length / 200));

  for (const enemy of allPlayers) {
    if (enemy.id === viewerPlayer.id) continue;
    if (!enemy.alive) continue;
    if (enemy.teamIndex === viewerPlayer.teamIndex) continue;
    for (let i = 0; i < pathPoints.length; i += step) {
      if (scannerSeesPlayer(pathPoints[i], enemy, planets, revealRadiusSq)) {
        spottedIds.add(enemy.id);
        break;
      }
    }
  }
  return Array.from(spottedIds);
}

/**
 * Determine which enemy players are visible to the viewer via LOS.
 * Skips players on the same team (they're allies, not enemies).
 */
export function getVisibleEnemies(
  viewerPlayer: PlayerState,
  allPlayers: PlayerState[],
  planets: PlanetState[],
  useWasm?: boolean,
  /** Phase 57: the match, so a guard's perception (range, cone, alert level) can narrow the result. Omit = line of sight alone. */
  perception?: GameState,
): number[] {
  const ids = getVisibleEnemiesByLos(viewerPlayer, allPlayers, planets, useWasm);
  return perception ? perceptionFilter(perception, viewerPlayer, ids) : ids;
}

function getVisibleEnemiesByLos(
  viewerPlayer: PlayerState,
  allPlayers: PlayerState[],
  planets: PlanetState[],
  useWasm?: boolean,
): number[] {
  if (useWasm) {
    // Filter to alive, non-teammate enemies for WASM
    const enemies = allPlayers.filter(
      p => p.id !== viewerPlayer.id && p.alive && p.teamIndex !== viewerPlayer.teamIndex
    );
    if (enemies.length > 0) {
      const viewerEye = losEyePoint(viewerPlayer, planets);
      const result = wasmGetVisibleEnemies(viewerEye, viewerPlayer.planetIndex, enemies, planets);
      if (result) return result;
    } else {
      return [];
    }
  }

  // Eye to eye, through exact terrain: planet bodies, craters and ridges all block alike.
  const viewerEye = losEyePoint(viewerPlayer, planets);
  const visibleIds: number[] = [];
  for (const enemy of allPlayers) {
    if (enemy.id === viewerPlayer.id) continue;
    if (!enemy.alive) continue;
    if (enemy.teamIndex === viewerPlayer.teamIndex) continue;
    if (hasLineOfSight(viewerEye, losEyePoint(enemy, planets), planets)) {
      visibleIds.push(enemy.id);
    }
  }
  return visibleIds;
}

// =============================================================================
// TEAM FOG (DATALINKED LOS)
// =============================================================================

/**
 * Merge fog states across all alive teammates.
 * A vertex is revealed if ANY teammate revealed it.
 * Uses the most recent snapshot (highest revealTurn) for each vertex.
 */
export function getTeamFog(
  playerIndex: number,
  fog: FogState[],
  players: PlayerState[]
): FogState {
  const player = players[playerIndex];
  const teamIndices: number[] = [];
  for (let i = 0; i < players.length; i++) {
    if (players[i].teamIndex === player.teamIndex && players[i].alive) {
      teamIndices.push(i);
    }
  }

  // If solo team member, just return own fog
  if (teamIndices.length <= 1) return fog[playerIndex];

  const baseFog = fog[playerIndex];
  const mergedPlanets: FogPlanetState[] = baseFog.planets.map((fp, pi) => {
    const revealedVertices = [...fp.revealedVertices];
    const terrainSnapshot = new Float32Array(fp.terrainSnapshot);
    const colorSnapshot = new Float32Array(fp.colorSnapshot);
    const revealTurn = new Uint16Array(fp.revealTurn);

    // Merge from each teammate's fog
    for (const ti of teamIndices) {
      if (ti === playerIndex) continue;
      const teammateFog = fog[ti]?.planets[pi];
      if (!teammateFog) continue;

      for (let v = 0; v < revealedVertices.length; v++) {
        if (teammateFog.revealedVertices[v]) {
          // Use the most recent snapshot
          if (!revealedVertices[v] || teammateFog.revealTurn[v] > revealTurn[v]) {
            revealedVertices[v] = true;
            terrainSnapshot[v] = teammateFog.terrainSnapshot[v];
            colorSnapshot[v * 3] = teammateFog.colorSnapshot[v * 3];
            colorSnapshot[v * 3 + 1] = teammateFog.colorSnapshot[v * 3 + 1];
            colorSnapshot[v * 3 + 2] = teammateFog.colorSnapshot[v * 3 + 2];
            revealTurn[v] = teammateFog.revealTurn[v];
          }
        }
      }
    }

    return { revealedVertices, terrainSnapshot, colorSnapshot, revealTurn };
  });

  // Merge ghost markers across teammates
  const ghostMap = new Map<number, GhostMarker>();
  for (const ti of teamIndices) {
    const teammateFog = fog[ti];
    if (!teammateFog) continue;
    for (const ghost of teammateFog.ghosts) {
      const existing = ghostMap.get(ghost.playerId);
      if (!existing || ghost.turnSeen > existing.turnSeen) {
        ghostMap.set(ghost.playerId, ghost);
      }
    }
  }

  // Merge structure ghosts across teammates
  const structGhostMap = new Map<number, StructureGhost>();
  for (const ti of teamIndices) {
    const teammateFog = fog[ti];
    if (!teammateFog) continue;
    for (const sg of teammateFog.structureGhosts) {
      const existing = structGhostMap.get(sg.structureId);
      if (!existing || sg.turnSeen > existing.turnSeen) {
        structGhostMap.set(sg.structureId, sg);
      }
    }
  }

  // Merge exposures (just use own player's exposures, not teammates')
  const exposures = fog[playerIndex]?.exposures ?? [];

  return {
    planets: mergedPlanets,
    ghosts: [...ghostMap.values()],
    structureGhosts: [...structGhostMap.values()],
    exposures,
  };
}

/**
 * Check LOS from ALL alive teammates' positions and union the visible enemy IDs.
 */
export function getTeamVisibleEnemies(
  viewerPlayer: PlayerState,
  allPlayers: PlayerState[],
  planets: PlanetState[],
  useWasm?: boolean,
  perception?: GameState,
): number[] {
  const visibleSet = new Set<number>();

  // Get LOS from each alive teammate
  for (const teammate of allPlayers) {
    if (teammate.teamIndex !== viewerPlayer.teamIndex) continue;
    if (!teammate.alive) continue;

    const enemyIds = getVisibleEnemies(teammate, allPlayers, planets, useWasm, perception);
    for (const id of enemyIds) {
      visibleSet.add(id);
    }
  }

  return [...visibleSet];
}

// =============================================================================
// GHOST MARKERS
// =============================================================================

/**
 * Update ghost markers based on currently visible enemies.
 * Visible enemies get their ghost updated to current position/turn.
 * Non-visible enemies keep their existing ghost marker.
 */
export function updateGhostMarkers(
  fogState: FogState,
  visibleEnemyIds: number[],
  allPlayers: PlayerState[],
  planets: PlanetState[],
  turnNumber: number
): FogState {
  const visibleSet = new Set(visibleEnemyIds);
  const newGhosts: GhostMarker[] = [];

  // Keep existing ghosts for enemies NOT currently visible
  for (const ghost of fogState.ghosts) {
    if (!visibleSet.has(ghost.playerId)) {
      // Keep the old ghost (stale position)
      newGhosts.push(ghost);
    }
  }

  // Create/update ghosts for currently visible enemies
  for (const enemyId of visibleEnemyIds) {
    const enemy = allPlayers.find(p => p.id === enemyId);
    if (!enemy || !enemy.alive) continue;

    const pos = getPlayerWorldPos(enemy, planets);
    newGhosts.push({
      playerId: enemyId,
      position: { x: pos.x, y: pos.y, z: pos.z },
      turnSeen: turnNumber,
    });
  }

  return { ...fogState, ghosts: newGhosts };
}

// =============================================================================
// STRUCTURE SPOTTING
// =============================================================================

/**
 * Find enemy structures within reveal radius of a single point.
 * Used for real-time structure detection during probe flight.
 */
export function getStructuresAtPoint(
  viewerPlayer: PlayerState,
  structures: StructureState[],
  allPlayers: PlayerState[],
  planets: PlanetState[],
  point: Vec3,
  revealRadius: number
): number[] {
  const revealRadiusSq = revealRadius * revealRadius;
  const spottedIds: number[] = [];

  for (const structure of structures) {
    if (structure.health <= 0) continue;

    // Skip friendly structures
    const owner = allPlayers.find(p => p.id === structure.ownerId);
    if (!owner || owner.teamIndex === viewerPlayer.teamIndex) continue;

    const planet = planets[structure.planetIndex];
    if (!planet || !planet.alive) continue;

    if (scannerSeesPoint(point, eyePointAt(planet, structure.surfaceDir), planets, revealRadiusSq)) {
      spottedIds.push(structure.id);
    }
  }

  return spottedIds;
}

/** In range of the scanner and not behind terrain. */
function scannerSeesPoint(point: Vec3, target: Vec3, planets: PlanetState[], revealRadiusSq: number): boolean {
  const dx = target.x - point.x, dy = target.y - point.y, dz = target.z - point.z;
  if (dx * dx + dy * dy + dz * dz > revealRadiusSq) return false;
  return hasLineOfSight(point, target, planets);
}

/**
 * Find enemy structures within reveal radius of any point along a probe's path.
 */
export function getStructuresAlongPath(
  viewerPlayer: PlayerState,
  structures: StructureState[],
  allPlayers: PlayerState[],
  planets: PlanetState[],
  pathPoints: Vec3[],
  revealRadius: number
): number[] {
  const revealRadiusSq = revealRadius * revealRadius;
  const spottedIds: Set<number> = new Set();

  const step = Math.max(1, Math.floor(pathPoints.length / 200));

  for (const structure of structures) {
    if (structure.health <= 0) continue;

    const owner = allPlayers.find(p => p.id === structure.ownerId);
    if (!owner || owner.teamIndex === viewerPlayer.teamIndex) continue;

    const planet = planets[structure.planetIndex];
    if (!planet || !planet.alive) continue;

    const structTop = eyePointAt(planet, structure.surfaceDir);
    for (let i = 0; i < pathPoints.length; i += step) {
      if (scannerSeesPoint(pathPoints[i], structTop, planets, revealRadiusSq)) {
        spottedIds.add(structure.id);
        break;
      }
    }
  }

  return Array.from(spottedIds);
}

/**
 * Create/update structure ghost markers from spotted structure IDs.
 * Preserves existing ghosts for unspotted structures.
 */
export function updateStructureGhosts(
  fogState: FogState,
  spottedStructureIds: number[],
  structures: StructureState[],
  planets: PlanetState[],
  turnNumber: number
): FogState {
  const spottedSet = new Set(spottedStructureIds);
  const newGhosts: StructureGhost[] = [];

  // Keep existing ghosts for structures NOT currently spotted
  for (const ghost of fogState.structureGhosts) {
    if (!spottedSet.has(ghost.structureId)) {
      newGhosts.push(ghost);
    }
  }

  // Create/update ghosts for currently spotted structures
  for (const structId of spottedStructureIds) {
    const structure = structures.find(s => s.id === structId);
    if (!structure || structure.health <= 0) continue;

    const planet = planets[structure.planetIndex];
    if (!planet || !planet.alive) continue;

    const dir = normalize(structure.surfaceDir);
    const pos = add(planet.position, scale(dir, planet.radius));

    newGhosts.push({
      structureId: structId,
      ownerId: structure.ownerId,
      structureType: structure.type,
      position: { x: pos.x, y: pos.y, z: pos.z },
      turnSeen: turnNumber,
    });
  }

  return { ...fogState, structureGhosts: newGhosts };
}

// =============================================================================
// SPOTTED DETECTION
// =============================================================================

/**
 * Check if a player is visible to any enemy team.
 * Returns array of team indices that can see this player.
 */
export function checkIfPlayerSpotted(
  player: PlayerState,
  allPlayers: PlayerState[],
  planets: PlanetState[],
  useWasm?: boolean,
): number[] {
  const spottingTeams: number[] = [];
  const checkedTeams = new Set<number>();

  for (const other of allPlayers) {
    if (!other.alive) continue;
    if (other.teamIndex === player.teamIndex) continue;
    if (checkedTeams.has(other.teamIndex)) continue;

    // Check if this enemy team member has LOS to the player
    const enemyIds = getVisibleEnemies(other, allPlayers, planets, useWasm);
    if (enemyIds.includes(player.id)) {
      spottingTeams.push(other.teamIndex);
      checkedTeams.add(other.teamIndex); // Found a spotter, skip remaining members
    }
  }

  return spottingTeams;
}

// =============================================================================
// STALENESS
// =============================================================================

/**
 * Brightness multiplier for terrain revealed `turnsStale` turns ago: full for
 * FOG_STALE_TURNS, then a straight fade over FOG_STALE_FADE_TURNS down to
 * FOG_STALE_MIN_BRIGHTNESS. (The old curve reached 0.7 after 25 turns —
 * nothing ever looked stale.)
 */
export function fogStalenessDim(turnsStale: number): number {
  if (turnsStale <= FOG_STALE_TURNS) return 1;
  const t = Math.min(1, (turnsStale - FOG_STALE_TURNS) / FOG_STALE_FADE_TURNS);
  return 1 - (1 - FOG_STALE_MIN_BRIGHTNESS) * t;
}

// =============================================================================
// MARKER LIFECYCLE
// =============================================================================

/**
 * Forget what can no longer be acted on: ghosts of dead players, ghosts older
 * than `maxGhostAge` turns, and ghosts of destroyed structures. Run once per
 * turn by resolveTurnStart for every player's fog. Returns the same object
 * when nothing changed.
 */
export function pruneFogMarkers(
  fogState: FogState,
  players: PlayerState[],
  structures: StructureState[],
  turnNumber: number,
  maxGhostAge: number,
): FogState {
  const alive = new Set<number>();
  for (const p of players) if (p.alive) alive.add(p.id);
  const standing = new Set<number>();
  for (const s of structures) if (s.health > 0) standing.add(s.id);

  const ghosts = fogState.ghosts.filter(g => alive.has(g.playerId) && turnNumber - g.turnSeen <= maxGhostAge);
  const structureGhosts = fogState.structureGhosts.filter(
    g => standing.has(g.structureId) && turnNumber - g.turnSeen <= maxGhostAge,
  );
  if (ghosts.length === fogState.ghosts.length && structureGhosts.length === fogState.structureGhosts.length) {
    return fogState;
  }
  return { ...fogState, ghosts, structureGhosts };
}

// =============================================================================
// EXPOSURE MARKERS
// =============================================================================

/** Two exposures closer than this are the same spot: refresh, don't stack. */
const EXPOSURE_MERGE_DISTANCE = 1.0;

/**
 * Update exposure markers ("where enemies saw me"). Drops markers older than
 * `maxAge`; when spotted, refreshes the marker at this spot or adds one.
 */
export function updateExposureMarkers(
  fogState: FogState,
  spotted: boolean,
  spottedBy: 'los' | 'probe',
  playerPos: Vec3,
  turnNumber: number,
  maxAge: number
): FogState {
  const exposures = fogState.exposures.filter(
    e => turnNumber - e.turnSeen <= maxAge
  );

  if (spotted) {
    const marker: ExposureMarker = {
      position: { x: playerPos.x, y: playerPos.y, z: playerPos.z },
      turnSeen: turnNumber,
      spottedBy,
    };
    const mergeSq = EXPOSURE_MERGE_DISTANCE * EXPOSURE_MERGE_DISTANCE;
    const existing = exposures.findIndex(e => {
      const dx = e.position.x - playerPos.x, dy = e.position.y - playerPos.y, dz = e.position.z - playerPos.z;
      return dx * dx + dy * dy + dz * dz < mergeSq;
    });
    if (existing >= 0) exposures[existing] = marker;
    else exposures.push(marker);
  }

  return { ...fogState, exposures };
}

// =============================================================================
// HELPERS
// =============================================================================

/** Where a player stands (exact terrain): probe distance checks and ghost markers. */
const getPlayerWorldPos = playerSurfacePoint;
